import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { Loading, StatusBadge, SectionCard } from "@/components/common/Primitives";
import { ContractForm } from "@/components/forms/ContractForm";
import { fmtEUR, fmtDate } from "@/lib/format";
import { CONTRACT_STATUS_MAP, RENEWAL_STAGE_MAP, CATEGORY_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Edit, AlertTriangle, ShieldCheck, Calendar } from "lucide-react";

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);

  const { data: reg } = useQuery({ queryKey: ["registry"], queryFn: async () => (await api.get("/settings/registry")).data });
  const { data, isLoading } = useQuery({
    queryKey: ["contract", id],
    queryFn: async () => (await api.get(`/contracts/${id}`)).data,
  });

  if (isLoading) return <Loading />;
  if (!data) return null;
  const c = data.contract;
  const dl = c.deadlines || {};
  const refetch = () => qc.invalidateQueries({ queryKey: ["contract", id] });

  const changeStatus = async (status) => {
    try { await api.post(`/contracts/${id}/status`, { status }); toast.success("Status geändert"); refetch(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const Row = ({ label, value, verified }) => (
    <div className="flex items-center justify-between gap-4 py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="flex items-center gap-1.5 text-right font-medium">
        {value || "—"}
        {verified === false && value && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" title="Nicht geprüft" />}
      </span>
    </div>
  );

  return (
    <div>
      <button onClick={() => navigate("/vertraege")} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-contracts">
        <ArrowLeft className="h-4 w-4" /> Zurück zu Verträgen
      </button>

      <div className="mb-6 flex flex-col gap-4 rounded-lg border border-border bg-card p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{c.provider} · {c.tariff_name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="font-mono">{c.contract_number}</span>
            <Badge variant="secondary">{CATEGORY_LABELS[c.category] || c.category}</Badge>
            <StatusBadge status={c.status} map={CONTRACT_STATUS_MAP} />
            <button onClick={() => navigate(`/kunden/${c.customer_id}`)} className="underline hover:text-foreground">{c.customer_name}</button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {can(user?.role, "contracts:write") && (
            <Select value={c.status} onValueChange={changeStatus}>
              <SelectTrigger className="w-48" data-testid="contract-status-change"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(CONTRACT_STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {can(user?.role, "contracts:write") && <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="edit-contract-button"><Edit className="mr-1.5 h-4 w-4" />Bearbeiten</Button>}
        </div>
      </div>

      {dl.needs_verification && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
          <AlertTriangle className="h-4 w-4" /> Fristen konnten nicht zuverlässig bestimmt werden und benötigen eine manuelle Prüfung.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard title="Vertragsdaten">
          <Row label="Anbieter" value={c.provider} />
          <Row label="Anbieter-Vertragsnr." value={c.provider_contract_number} />
          <Row label="Auftragsnummer" value={c.order_number} />
          <Row label="Monatliche Gebühr" value={fmtEUR(c.monthly_fee)} />
          <Row label="Einmalkosten" value={fmtEUR(c.one_time_charges)} />
          <Row label="Laufzeit" value={`${c.term_months} Monate`} />
          <Row label="Kündigungsfrist" value={c.cancellation_notice_days ? `${c.cancellation_notice_days} Tage` : null} />
        </SectionCard>

        <SectionCard title="Fristen (deterministisch berechnet)">
          <div className="mb-3 flex items-center gap-2 text-xs">
            {c.deadline_verified ? <span className="flex items-center gap-1 text-emerald-600"><ShieldCheck className="h-3.5 w-3.5" />Geprüft</span>
              : <span className="flex items-center gap-1 text-amber-600"><AlertTriangle className="h-3.5 w-3.5" />Nicht bestätigt</span>}
          </div>
          <Row label="Vertragsende" value={fmtDate(dl.contract_end)} />
          <Row label="Frühestmögliche Kündigung" value={fmtDate(dl.earliest_cancellation)} />
          <Row label="Kündigungsfrist-Stichtag" value={dl.cancellation_deadline ? fmtDate(dl.cancellation_deadline) : null} verified={!dl.needs_verification} />
          <Row label="Verlängerung ab" value={fmtDate(dl.renewal_eligible_from)} />
          <div className="mt-3 flex items-center gap-2 rounded-md bg-muted/50 px-3 py-2 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            {c.days_to_end != null ? `Noch ${c.days_to_end} Tage bis Vertragsende` : "Kein Enddatum hinterlegt"}
          </div>
        </SectionCard>
      </div>

      {c.renewal_history?.length > 0 && (
        <SectionCard title="Verlängerungsverlauf" className="mt-4">
          <div className="space-y-2">
            {c.renewal_history.map((h, i) => (
              <div key={i} className="flex items-center justify-between gap-3 text-sm">
                <StatusBadge status={h.status} map={RENEWAL_STAGE_MAP} />
                <span className="text-muted-foreground">{h.by} · {fmtDate(h.at)}</span>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {c.notes && <SectionCard title="Notizen" className="mt-4"><p className="text-sm">{c.notes}</p></SectionCard>}

      <ContractForm open={editOpen} onOpenChange={setEditOpen} contract={c} registry={reg} onSaved={refetch} />
    </div>
  );
}
